import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Card,
  Descriptions,
  Tag,
  Button,
  Space,
  Modal,
  Input,
  message,
  Typography,
  Table,
  Progress,
  Row,
  Col,
  Statistic,
  Spin,
  Tooltip
} from 'antd';
import {
  ArrowLeftOutlined,
  PlayCircleOutlined,
  CheckCircleOutlined,
  FileExcelOutlined,
  EditOutlined,
  TeamOutlined,
  DollarOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import * as XLSX from 'xlsx';
import apiClient from '../services/api';
import CampaignWorkRecordsTable from '../components/maintenanceCampaign/CampaignWorkRecordsTable';
import { MaintenanceCampaign, AssetWorkRecord } from '../types';
import { formatVND } from '../utils/formatters';

const { Text, Title } = Typography;

const MaintenanceCampaignDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [campaign, setCampaign] = useState<MaintenanceCampaign | null>(null);
  const [workRecords, setWorkRecords] = useState<AssetWorkRecord[]>([]);
  const [approveExecutionModalVisible, setApproveExecutionModalVisible] = useState(false);
  const [approveCompletionModalVisible, setApproveCompletionModalVisible] = useState(false);
  const [approveExecutionLoading, setApproveExecutionLoading] = useState(false);
  const [approveCompletionLoading, setApproveCompletionLoading] = useState(false);
  const [executionDocumentCode, setExecutionDocumentCode] = useState('');
  const [completionDocumentCode, setCompletionDocumentCode] = useState('');

  useEffect(() => {
    if (id) {
      loadCampaignDetails();
    }
  }, [id]);

  const loadCampaignDetails = async () => {
    try {
      setLoading(true);
      const [campaignData, workRecordsData] = await Promise.all([
        apiClient.getMaintenanceCampaignById(Number(id)),
        apiClient.getMaintenanceCampaignWorkRecords(Number(id))
      ]);

      setCampaign(campaignData as MaintenanceCampaign);
      setWorkRecords(workRecordsData as AssetWorkRecord[]);
    } catch (error: any) {
      message.error('Không thể tải thông tin đợt tác nghiệp');
      console.error('Error loading campaign details:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending_execution_approval':
        return 'orange';
      case 'in_progress':
        return 'blue';
      case 'approved':
        return 'green';
      case 'rejected_editable':
        return 'red';
      case 'rejected_final':
        return 'red';
      default:
        return 'default';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'pending_execution_approval':
        return 'Chờ duyệt thực hiện';
      case 'in_progress':
        return 'Đang thực hiện';
      case 'approved':
        return 'Đã duyệt (hoàn thành)';
      case 'rejected_editable':
        return 'Từ chối (có thể sửa)';
      case 'rejected_final':
        return 'Từ chối (dứt điểm)';
      default:
        return status;
    }
  };

  const handleApproveExecution = async () => {
    if (!executionDocumentCode.trim()) {
      message.error('Vui lòng nhập mã hồ sơ duyệt thực hiện');
      return;
    }

    setApproveExecutionLoading(true);
    try {
      await apiClient.approveMaintenanceCampaignExecution(Number(id), {
        execution_document_code: executionDocumentCode
      });
      message.success('Đã duyệt thực hiện đợt tác nghiệp thành công!');
      setApproveExecutionModalVisible(false);
      setExecutionDocumentCode('');
      loadCampaignDetails();
    } catch (error: any) {
      message.error(error?.response?.data?.message || 'Không thể duyệt thực hiện đợt tác nghiệp');
    } finally {
      setApproveExecutionLoading(false);
    }
  };

  const handleApproveCompletion = async () => {
    if (!completionDocumentCode.trim()) {
      message.error('Vui lòng nhập mã hồ sơ duyệt hoàn thành');
      return;
    }

    setApproveCompletionLoading(true);
    try {
      await apiClient.approveMaintenanceCampaignCompletion(Number(id), {
        completion_document_code: completionDocumentCode
      });
      message.success('Đã duyệt hoàn thành đợt tác nghiệp thành công!');
      setApproveCompletionModalVisible(false);
      setCompletionDocumentCode('');
      loadCampaignDetails();
    } catch (error: any) {
      message.error(error?.response?.data?.message || 'Không thể duyệt hoàn thành đợt tác nghiệp');
    } finally {
      setApproveCompletionLoading(false);
    }
  };

  const handleExportExcel = () => {
    if (!workRecords.length) {
      message.warning('Không có dữ liệu để xuất Excel');
      return;
    }

    const exportData = workRecords.map(record => ({
      'Mã tài sản': record.asset_code,
      'Tên tài sản': record.asset_name,
      'Loại tác nghiệp': record.maintenance_type_name,
      'Trạng thái': getStatusText(record.status),
      'Ngày tạo': record.work_date ? dayjs(record.work_date).format('DD/MM/YYYY') : '—',
      'Ngày hoàn thành': record.completed_date ? dayjs(record.completed_date).format('DD/MM/YYYY') : '—',
      'Chi phí': record.cost ? formatVND(record.cost) : '—',
      'Mã hồ sơ': record.work_documents || '—',
      'Mô tả': record.work_description || '—'
    }));

    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Tác nghiệp');

    const fileName = `Tác nghiệp - ${campaign?.name || 'Campaign'} - ${dayjs().format('DD-MM-YYYY')}.xlsx`;
    XLSX.writeFile(wb, fileName);
  };

  // Xóa các function và biến không còn sử dụng
  // const workRecordColumns = [...]
  // const getWorkRecordStatusColor = ...
  // const getWorkRecordStatusText = ...


  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!campaign) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Title level={3}>Không tìm thấy đợt tác nghiệp</Title>
        <Button type="primary" onClick={() => navigate('/maintenance-campaigns')}>
          Quay lại danh sách
        </Button>
      </div>
    );
  }

  return (
    <div style={{ padding: 24 }}>
      <div style={{ marginBottom: 24, display: 'flex', alignItems: 'center', gap: 16 }}>
        <Button
          type="text"
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate('/maintenance-campaigns')}
          style={{ padding: 4, height: 'auto' }}
        />
        <Title level={2} style={{ margin: 0 }}>
          Chi tiết đợt tác nghiệp: {campaign.name}
        </Title>
      </div>

      <Row gutter={24} style={{ marginBottom: 24 }}>
        <Col span={6}>
          <Card>
            <Statistic
              title="Trạng thái"
              value={getStatusText(campaign.status)}
              valueStyle={{ color: getStatusColor(campaign.status) === 'green' ? '#52c41a' : '#1890ff' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Tổng tác nghiệp"
              value={workRecords.length}
              prefix={<TeamOutlined />}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Hoàn thành"
              value={workRecords.filter(r => r.status === 'completed').length}
              prefix={<CheckCircleOutlined />}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Tổng chi phí"
              value={formatVND(workRecords.reduce((sum, r) => sum + (r.cost || 0), 0))}
              prefix={<DollarOutlined />}
              valueStyle={{ color: '#fa8c16' }}
            />
          </Card>
        </Col>
      </Row>

      <Card title="Thông tin đợt tác nghiệp" style={{ marginBottom: 24 }}>
        <Descriptions bordered column={2}>
          <Descriptions.Item label="Tên đợt tác nghiệp">
            <Text strong style={{ color: '#1890ff' }}>
              {campaign.name}
            </Text>
          </Descriptions.Item>
          <Descriptions.Item label="Trạng thái">
            <Tag color={getStatusColor(campaign.status)}>
              {getStatusText(campaign.status)}
            </Tag>
          </Descriptions.Item>
          <Descriptions.Item label="Công ty">
            {campaign.company_name}
          </Descriptions.Item>
          <Descriptions.Item label="Địa điểm">
            {campaign.all_location_names && campaign.all_location_names !== campaign.location_name ? (
              <Tooltip title={`Bao gồm tài sản tại: ${campaign.all_location_names}`}>
                <span style={{ cursor: 'help', borderBottom: '1px dotted #ccc' }}>
                  {campaign.location_name || 'Nhiều vị trí'}*
                </span>
              </Tooltip>
            ) : (
              campaign.location_name || '—'
            )}
          </Descriptions.Item>
          <Descriptions.Item label="Người phụ trách">
            <Text style={{ color: '#1890ff' }} strong>
              {campaign.conducted_by_name}
            </Text>
          </Descriptions.Item>
          <Descriptions.Item label="Thời gian thực hiện">
            <div>
              <div>Từ: {dayjs(campaign.start_date).format('DD/MM/YYYY')}</div>
              <div>Đến: {dayjs(campaign.end_date).format('DD/MM/YYYY')}</div>
            </div>
          </Descriptions.Item>
          <Descriptions.Item label="Ngày tạo">
            {dayjs(campaign.created_at).format('DD/MM/YYYY HH:mm')}
          </Descriptions.Item>
          <Descriptions.Item label="Cập nhật lần cuối">
            {dayjs(campaign.updated_at).format('DD/MM/YYYY HH:mm')}
          </Descriptions.Item>
          {campaign.execution_document_code && (
            <>
              <Descriptions.Item label="Hồ sơ duyệt thực hiện">
                {campaign.execution_document_code}
              </Descriptions.Item>
              <Descriptions.Item label="Ngày duyệt thực hiện">
                {campaign.execution_approval_date ? dayjs(campaign.execution_approval_date).format('DD/MM/YYYY HH:mm') : '—'}
              </Descriptions.Item>
            </>
          )}
          {campaign.completion_document_code && (
            <>
              <Descriptions.Item label="Hồ sơ duyệt hoàn thành">
                {campaign.completion_document_code}
              </Descriptions.Item>
              <Descriptions.Item label="Ngày duyệt hoàn thành">
                {campaign.completion_approval_date ? dayjs(campaign.completion_approval_date).format('DD/MM/YYYY HH:mm') : '—'}
              </Descriptions.Item>
            </>
          )}
        </Descriptions>
      </Card>

      {/* Sử dụng component mới thay vì Card cũ */}
      <CampaignWorkRecordsTable
        campaignId={Number(id)}
        campaignName={campaign?.name || ''}
        onRefresh={loadCampaignDetails}
        campaignStatus={campaign?.status}
        onApproveExecution={() => setApproveExecutionModalVisible(true)}
        onApproveCompletion={() => setApproveCompletionModalVisible(true)}
        companyId={campaign?.company_id}
      />

      {/* Modal duyệt thực hiện */}
      <Modal
        title="Duyệt thực hiện đợt tác nghiệp"
        open={approveExecutionModalVisible}
        onOk={handleApproveExecution}
        onCancel={() => {
          setApproveExecutionModalVisible(false);
          setExecutionDocumentCode('');
        }}
        confirmLoading={approveExecutionLoading}
        okText="Duyệt"
        cancelText="Hủy"
      >
        <div style={{ marginBottom: 16 }}>
          <Text>Vui lòng nhập hồ sơ duyệt thực hiện từ văn phòng điện tử:</Text>
        </div>
        <Input
          placeholder="Nhập mã hồ sơ..."
          value={executionDocumentCode}
          onChange={(e) => setExecutionDocumentCode(e.target.value)}
        />
      </Modal>

      {/* Modal duyệt hoàn thành */}
      <Modal
        title="Duyệt hoàn thành đợt tác nghiệp"
        open={approveCompletionModalVisible}
        onOk={handleApproveCompletion}
        onCancel={() => {
          setApproveCompletionModalVisible(false);
          setCompletionDocumentCode('');
        }}
        confirmLoading={approveCompletionLoading}
        okText="Duyệt"
        cancelText="Hủy"
      >
        <div style={{ marginBottom: 16 }}>
          <Text>Vui lòng nhập hồ sơ duyệt hoàn thành từ văn phòng điện tử:</Text>
        </div>
        <Input
          placeholder="Nhập mã hồ sơ..."
          value={completionDocumentCode}
          onChange={(e) => setCompletionDocumentCode(e.target.value)}
        />
      </Modal>
    </div>
  );
};

export default MaintenanceCampaignDetailPage; 